import { useEffect, type ReactNode } from "react";

import { usePersistentFlag } from "./persistentUi";

/**
 * Chrome partagé pour replier un panneau latéral (cockpit, carte pilote).
 * `side` = le bord du panneau qui touche le contenu central : un panneau à
 * gauche a `side="right"`, un panneau à droite a `side="left"`. Les chevrons
 * pointent toujours vers le mouvement : replier = vers le bord, déplier = vers
 * le centre.
 */
type Side = "left" | "right";

const CHEVRON: Record<Side, { collapse: string; expand: string }> = {
  // panneau de droite : replier pousse à droite, déplier tire vers la gauche
  left: { collapse: "»", expand: "«" },
  // panneau de gauche : replier pousse à gauche, déplier tire vers la droite
  right: { collapse: "«", expand: "»" },
};

export function PanelTitleBar({
  title,
  side,
  onCollapse,
  right,
}: {
  title: string;
  side: Side;
  onCollapse: () => void;
  right?: ReactNode;
}) {
  const button = (
    <button
      type="button"
      className="button is-small is-white"
      aria-label={`Réduire le panneau ${title}`}
      title="Réduire ce panneau"
      onClick={onCollapse}
      style={{ height: 30, width: 30, padding: 0, flex: "none", lineHeight: 1, fontSize: "1.2rem", fontWeight: 700 }}
    >
      {CHEVRON[side].collapse}
    </button>
  );
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 12px 8px",
        borderBottom: "1px solid var(--sentinelle-rule)",
      }}
    >
      {side === "left" ? button : null}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          font: "700 12.5px/1 var(--bulma-family-secondary)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--sentinelle-ink)",
        }}
      >
        {title}
      </span>
      {right}
      {side === "right" ? button : null}
    </div>
  );
}

/**
 * Bande verticale étroite qui remplace un panneau replié : un bouton pour le
 * rouvrir et son nom écrit dans la hauteur. Le nom reste ainsi présent dans le
 * DOM (repère visuel et accessibilité) même panneau fermé.
 */
export function CollapsedRail({
  label,
  side,
  onExpand,
}: {
  label: string;
  side: Side;
  onExpand: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        padding: "8px 0",
        background: "var(--sentinelle-surface)",
        borderLeft: side === "left" ? "1px solid var(--sentinelle-rule)" : undefined,
        borderRight: side === "right" ? "1px solid var(--sentinelle-rule)" : undefined,
        // Bug trouvé par l'utilisateur (2026-09-11, carte pilote) : un panneau
        // sans `position` peint SOUS un voisin `position:relative` sans z-index
        // explicite (ex. le conteneur de la carte) dès que leurs boîtes se
        // recoupent, même si le voisin vient AVANT dans le DOM — c'est la règle
        // CSS « positionné bat non-positionné », indépendante de l'ordre du DOM.
        // Le bouton « Rouvrir » de ce rail se faisait donc recouvrir par les
        // boutons flottants de la carte. `position:relative` + z-index l'élève
        // dans le même contexte d'empilement, au-dessus (la carte plafonne à 6).
        position: "relative",
        zIndex: 10,
      }}
    >
      <button
        type="button"
        className="button is-small is-white"
        aria-label={`Rouvrir le panneau ${label}`}
        title={`Rouvrir « ${label} »`}
        onClick={onExpand}
        style={{ height: 30, width: 30, padding: 0, flex: "none", lineHeight: 1, fontSize: "1.2rem", fontWeight: 700 }}
      >
        {CHEVRON[side].expand}
      </button>
      <span
        className="ovl"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", color: "var(--sentinelle-ink-2)", whiteSpace: "nowrap" }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * Section d'outil **repliable**, **repliée par défaut** (§I) : évite de scroller
 * toute la liste des retournements pour atteindre l'outil voulu. État mémorisé
 * par navigateur. `badge` : chiffre optionnel (nb de solutions), contour rouge
 * clignotant s'il est > 0.
 */
export function CollapsibleTool({
  title,
  storageKey,
  badge,
  children,
}: {
  title: string;
  storageKey: string;
  badge?: number;
  children: ReactNode;
}) {
  const [open, setOpen] = usePersistentFlag(storageKey, false);
  return (
    <section style={{ borderTop: "1px solid var(--sentinelle-rule)" }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="ovl"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontWeight: 700,
          color: "var(--sentinelle-ink)",
        }}
      >
        <span aria-hidden style={{ fontSize: "0.95rem", width: 12 }}>
          {open ? "▾" : "▸"}
        </span>
        <span style={{ flex: 1, textAlign: "left" }}>{title}</span>
        {badge && badge > 0 ? (
          <span
            className="mono mae-tool-badge"
            aria-label={`${badge} solution${badge > 1 ? "s" : ""}`}
            style={{
              fontWeight: 800,
              fontSize: "0.7rem",
              minWidth: 18,
              textAlign: "center",
              padding: "0 5px",
              borderRadius: 9,
              color: "#fff",
              background: "var(--sentinelle-ok, #15803d)",
            }}
          >
            {badge}
          </span>
        ) : null}
      </button>
      {open ? <div style={{ padding: "0 14px 12px" }}>{children}</div> : null}
    </section>
  );
}

/**
 * Tiroir plein écran (demande utilisateur 2026-09-11 : responsive mobile) —
 * remplace une colonne latérale de grille CSS sous ~769px, où trois colonnes
 * fixes ne tiennent pas sur la largeur d'un téléphone. Même contenu que la
 * colonne desktop (Avance/Retard, Outils…), affiché en recouvrement plutôt
 * qu'à côté du centre. Ferme au tap sur le fond ou à l'échap, comme les
 * autres pop-ins de l'appli (`MapDeviationPanel`, popins de service…).
 */
export function MobileDrawer({
  children,
  onClose,
  side = "left",
}: {
  children: ReactNode;
  onClose: () => void;
  /** Bord d'ancrage du tiroir — assorti au bord du panneau qu'il remplace. */
  side?: "left" | "right";
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        display: "flex",
        background: "rgba(20,24,30,.4)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mae-scroll-y"
        style={{
          background: "var(--sentinelle-surface)",
          width: "min(92vw, 380px)",
          maxWidth: "100%",
          height: "100%",
          marginInlineStart: side === "right" ? "auto" : 0,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 0 28px rgba(0,0,0,.4)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
